import './filterDialog.scss';
import { If, CustomDialog, Loading } from 'common/src/components';
import { useImmer } from 'use-immer';
import { lang } from '@umu/ukit-lang';
import { Button, Modal } from '@umu/umu-design';
import { formatDepartmentName } from '../../utils';
import { useInfiniteScroll, useUpdateEffect } from 'ahooks';
import { CHECKBOX_CHECK_TYPE, Checkbox } from '../list/listItem';
import { IListItem, IMemberListInfo, MemberItem, RoleType } from '../../type';
import { getMemberList as getMemberListService } from '@umu/umu-api';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { getSugMemberList as getSugMemberListService } from '../../service';

import {
    filterDialogDefaultMemberList,
    isLoadEnd,
    limitExcludeUmuIdsCount,
    normMemberItem,
    renderMemberCount,
} from '../../dataflow';
import FuzzySearch from '../../../fuzzySearch';
import UserAvatar from '../../../user/userAvatar/userAvatar';

type DialogProps = {
    enterpriseName: string;
    departmentItem: IListItem;
    onClickFilter: (params: IListItem) => void;
};

const { confirm } = Modal;

const Dialog = (props: DialogProps & { closeDialog: () => void }) => {
    const { closeDialog, enterpriseName, departmentItem: propDepartmentItem, onClickFilter } = props;

    const keywordRef = useRef('');
    const infiniteScrollRef = useRef<any>();
    const [keyword, setKeyword] = useState('');
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [departmentItem, setDepartmentItem] = useImmer(propDepartmentItem);
    const memberListRequestIdRef = useRef(Symbol()); // 请求唯一标识, 用于解决竞态问题
    const [memberList, setMemberList] = useImmer<MemberItem>(filterDialogDefaultMemberList);

    /**
     * @description: MemberList 无线滚动
     * memberList 列表  -- 正常数据
     * subMemberList 列表  --  搜索数据
     */
    const {
        data: infiniteScrollData,
        reload: infiniteScrollReload,
        loading: infiniteScrollFirstLoading,
        loadingMore: infiniteScrollMoreLoading,
    } = useInfiniteScroll(
        (listInfo) => {
            if (listInfo == undefined) {
                // listInfo 为 undefined 说明为  正常数据 / 搜索数据 的首次请求, 因此在这个时候重置 唯一请求 ID。备注: 正常请求下拉加载复用请求 ID
                memberListRequestIdRef.current = Symbol();
            }
            const page_info = listInfo ? listInfo.page_info : filterDialogDefaultMemberList.page_info;

            return isSearchMode
                ? getSugMemberList(memberListRequestIdRef.current, page_info)
                : getMemberList(memberListRequestIdRef.current, page_info);
        },
        {
            manual: true,
            target: infiniteScrollRef,
            isNoMore: (listInfo) => {
                if (listInfo) {
                    return isLoadEnd(listInfo.page_info);
                }
                return false;
            },
        },
    );

    const handleConfirm = () => {
        onClickFilter(departmentItem);
        closeDialog();
    };

    const onInputClear = () => {
        setKeyword('');
        keywordRef.current = '';
        onSearch();
    };

    const onSearch = () => {
        if (keywordRef.current) {
            isSearchMode ? infiniteScrollReload() : setIsSearchMode(true);
        } else {
            !isSearchMode ? infiniteScrollReload() : setIsSearchMode(false);
        }
    };

    const validateExcludesIds = () => {
        const excludeUmuIdsCount = departmentItem?.oriData?.exclude_umu_ids?.length || 0;
        if (excludeUmuIdsCount + 1 > limitExcludeUmuIdsCount) {
            confirm({
                title: lang('操作超限'),
                content: lang('可支持取消勾选的账户数量上限为${0}，本次操作不生效。').replace(
                    '${0}',
                    limitExcludeUmuIdsCount,
                ),
                okText: lang('知道了'),
                centered: true,
                zIndex: 99999,
                onOk() {
                    // console.log();
                },
            });
            return false;
        }
        return true;
    };

    const onChangeSearch = (data: ChangeEvent<HTMLInputElement>) => {
        setKeyword(data.target.value);
        keywordRef.current = data.target.value;
    };

    const updateMemberList = (listInfo: IMemberListInfo) => {
        const exclude_umu_ids = departmentItem.oriData.exclude_umu_ids;
        const transformList = listInfo.list.map((item) => {
            return Object.assign(normMemberItem(item, !exclude_umu_ids.includes(item.id || item?.user_info?.umu_id)));
        });

        setMemberList((draft) => {
            draft.page_info = listInfo.page_info;
            draft.list = transformList;
        });
    };

    const getSugMemberList = async (requestId: Symbol, page_info: IPageInfo): Promise<IMemberListInfo> => {
        try {
            const params = {
                keyword: keywordRef.current,
                department_id: departmentItem.id,
                page: page_info.current_page + 1,
                size: page_info.size,
                exclude_resigned_user: 1, //1为过滤离职用户，0不过滤离职用户
            };
            const listInfo: IMemberListInfo = await getSugMemberListService(params);
            if (!listInfo || !listInfo.list) {
                return filterDialogDefaultMemberList;
            }
            return { ...listInfo, requestId };
        } catch (error) {
            console.log('error', error);
            return filterDialogDefaultMemberList;
        }
    };

    const getMemberList = async (requestId: Symbol, page_info: IPageInfo): Promise<IMemberListInfo> => {
        try {
            const params = {
                department_id: departmentItem.id,
                page: page_info.current_page + 1,
                size: page_info.size,
            };

            const listInfo: IMemberListInfo = await getMemberListService(params);
            if (!listInfo || !listInfo.list) {
                return filterDialogDefaultMemberList;
            }
            return { ...listInfo, requestId };
        } catch (error) {
            console.log('error', error);
            return filterDialogDefaultMemberList;
        }
    };

    const onCheckboxChange = (checked: boolean, member: IListItem, index: number) => {
        if (!validateExcludesIds()) {
            return;
        }
        setDepartmentItem((draft) => {
            const i = draft.oriData.exclude_umu_ids.indexOf(member.id);
            if (!checked && i === -1) {
                draft.oriData.exclude_umu_ids.push(member.id);
            } else if (checked && i !== -1) {
                draft.oriData.exclude_umu_ids.splice(i, 1);
            }
        });

        setMemberList((draft) => {
            draft.list[index].isSelected = checked;
        });
    };

    const renderDepartmentName = () => {
        return formatDepartmentName(
            enterpriseName,
            departmentItem.name,
            departmentItem.id,
            departmentItem.oriData && departmentItem.oriData.parent_path,
        );
    };

    const renderMemberItem = (member: IListItem, index: number) => {
        return (
            <div
                key={member.id}
                className="member-item"
                onClick={() => onCheckboxChange(!member.isSelected, member, index)}
            >
                <div className="member-item-content">
                    <div className="content-check">
                        <Checkbox
                            isChecked={member.isSelected ? CHECKBOX_CHECK_TYPE.IS_CHECK : CHECKBOX_CHECK_TYPE.UN_CHECK}
                            onChange={() => {
                                // console.log();
                            }}
                            disabledCheck={false}
                        />
                    </div>
                    <div className="avatar">
                        <UserAvatar src={member.avatar || ''} className="data-user-avatar" width={24} />
                    </div>
                    <span className="name  name-left">
                        {member.name}
                        {Number(member.oriData.role) === RoleType.admin ? (
                            <span className="role">{lang('负责人')}</span>
                        ) : null}
                    </span>
                </div>
            </div>
        );
    };

    useEffect(() => {
        infiniteScrollReload();
    }, [isSearchMode]);

    useUpdateEffect(() => {
        if (infiniteScrollData && memberListRequestIdRef.current === infiniteScrollData.requestId) {
            updateMemberList(infiniteScrollData as any);
        }
    }, [infiniteScrollData]);

    return (
        <div className="task-assign-department-select-filter-dialog-body">
            <h3 className="title">{lang('筛选已选账户')}</h3>
            <div className="content">
                <div className="department">
                    <span className="department-label">
                        {lang('部门')}
                        {lang('：')}
                    </span>
                    <span className="department-value">{renderDepartmentName()}</span>
                    <span className="department-member-count">
                        {lang('（')}
                        {renderMemberCount(departmentItem)}
                        {lang('）')}
                    </span>
                </div>
                <div className="account-title">
                    {lang('筛选已选账户')}
                    {lang('：')}
                </div>
                <div className="member-list-search-container">
                    <div className="member-list-search">
                        <div className="search">
                            <FuzzySearch
                                onInputClear={onInputClear}
                                placeholder={lang('搜索')}
                                onChangeSearch={onChangeSearch}
                                onSearch={onSearch}
                                keyword={keyword}
                                className="member-list-search-fuzzy-search"
                            />
                        </div>
                        <div className="member-list" ref={infiniteScrollRef}>
                            {infiniteScrollFirstLoading ? (
                                <Loading />
                            ) : infiniteScrollData ? (
                                memberList.list.map((item, index) => {
                                    return renderMemberItem(item, index);
                                })
                            ) : null}
                            <If condition={infiniteScrollMoreLoading}>
                                <div className="member-list-more-loading">
                                    <Loading />
                                </div>
                            </If>
                            <If condition={memberList.list.length === 0 && !infiniteScrollFirstLoading}>
                                <div className="member-list-empty">{lang('暂无搜索结果')}</div>
                            </If>
                        </div>
                    </div>
                </div>
            </div>
            <div className="operation">
                <Button type="ghost-grey" onClick={closeDialog}>
                    {lang('取消')}
                </Button>
                <Button type="primary" onClick={handleConfirm}>
                    {lang('确认')}
                </Button>
            </div>
        </div>
    );
};

export const FilterDialog = (props: DialogProps) => {
    const dialog = CustomDialog({
        className: 'task-assign-department-select-filter-dialog',
        canClose: true,
        width: '500px',
        childProps: {
            ...props,
            closeDialog: () => {
                dialog.destroy();
            },
        },
        contentComponent: Dialog,
    });
    return dialog;
};
